import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Button, Typography, Row, Col, Card, Divider, InputNumber, Space, message, DatePicker, theme } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { employeesService, userService, companiesService, folioSerieService, receiversService } from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const CreateNomina = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [receivers, setReceivers] = useState([]);
  const [folioSeries, setFolioSeries] = useState([]);
  const [loadingReceivers, setLoadingReceivers] = useState(false);

  const [totals, setTotals] = useState({
    totalPercepciones: 0,
    totalDeducciones: 0,
    totalISR: 0,
    totalOtrosPagos: 0,
    nominaTotal: 0,
  });
  const [loadedInvoiceId, setLoadedInvoiceId] = useState(null);
  const [loadedInvoiceData, setLoadedInvoiceData] = useState(null);

  // Normalizar Nomina para comparación (remueve undefined y ordena listas)
  const normalizeNominaForCompare = (nominaObj) => {
    const sortArr = (arr, keys) =>
      [...(arr || [])].sort((a, b) => {
        const ak = keys.map(k => (a?.[k] ?? '')).join('|');
        const bk = keys.map(k => (b?.[k] ?? '')).join('|');
        return ak.localeCompare(bk);
      });
    const stripUndefined = (o) => {
      if (o === null || typeof o !== 'object') return o;
      if (Array.isArray(o)) return o.map(stripUndefined);
      return Object.fromEntries(
        Object.entries(o)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, stripUndefined(v)])
      );
    };
    const n = stripUndefined(nominaObj || {});
    if (n.Percepciones && Array.isArray(n.Percepciones.Percepcion)) {
      n.Percepciones.Percepcion = sortArr(n.Percepciones.Percepcion, ['TipoPercepcion', 'Clave']);
    }
    if (n.Deducciones && Array.isArray(n.Deducciones.Deduccion)) {
      n.Deducciones.Deduccion = sortArr(n.Deducciones.Deduccion, ['TipoDeduccion', 'Clave']);
    }
    if (n.OtrosPagos && Array.isArray(n.OtrosPagos.OtroPago)) {
      n.OtrosPagos.OtroPago = sortArr(n.OtrosPagos.OtroPago, ['TipoOtroPago', 'Clave']);
    }
    if (n.Incapacidades && Array.isArray(n.Incapacidades.Incapacidad)) {
      n.Incapacidades.Incapacidad = sortArr(n.Incapacidades.Incapacidad, ['TipoIncapacidad', 'DiasIncapacidad']);
    }
    return n;
  };

  const stableStringify = (obj) => {
    const allKeys = [];
    JSON.stringify(obj, (key, value) => { allKeys.push(key); return value; });
    allKeys.sort();
    return JSON.stringify(obj, allKeys);
  };

  const hasNominaChanges = (dbNomina, payloadNomina) => {
    const a = normalizeNominaForCompare(dbNomina);
    const b = normalizeNominaForCompare(payloadNomina);
    return stableStringify(a) !== stableStringify(b);
  };

  // Calcula antigüedad ISO (PnYnMnD) entre fecha inicio y fecha fin
  const computeAntiguedadISO = (startDateStr, endDateStr) => {
    if (!startDateStr) return 'P0D';
    const start = dayjs(startDateStr);
    const end = dayjs(endDateStr || dayjs().format('YYYY-MM-DD'));
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) return 'P0D';

    let s = start;
    const years = end.diff(s, 'year');
    s = s.add(years, 'year');
    const months = end.diff(s, 'month');
    s = s.add(months, 'month');
    const days = end.diff(s, 'day');

    const parts = [];
    if (years > 0) parts.push(`${years}Y`);
    if (months > 0) parts.push(`${months}M`);
    parts.push(`${days}D`); // incluir días (puede ser 0)
    return `P${parts.join('')}`;
  };

  useEffect(() => {
    fetchCompanies();
    fetchReceivers();
  }, []);

  // Precarga de borrador si viene desde "Ver" con invoiceId
  useEffect(() => {
    const invoiceId = location.state?.invoiceId;
    if (invoiceId) {
      preloadDraft(invoiceId);
    }
  }, [location.state]);

  const preloadDraft = async (invoiceId) => {
    setLoading(true);
    try {
      const resp = await userService.getInvoice(invoiceId);
      const invoice = resp?.data || resp;

      // Asegurar folios para la empresa
      if (invoice?.company_id) {
        await fetchFolioSeries(invoice.company_id);
      }

      // Prellenar datos comunes del CFDI
      form.setFieldsValue({
        company_id: invoice?.company_id || undefined,
        folio_serie_id: invoice?.folio_serie_id || undefined,
        metodo_pago: invoice?.metodo_pago || 'PUE',
        uso_cfdi: invoice?.uso_cfdi || 'CN01',
        lugar_expedicion: invoice?.lugar_expedicion || invoice?.company?.tax_location || undefined,
      });

      // Intentar seleccionar el empleado basado en el RFC del receptor
      let employeeIdToSet = undefined;
      try {
        const taxId = invoice?.receiver?.tax_id || '';
        if (taxId) {
          const empResp = await employeesService.getEmployees(taxId);
          const list = empResp?.employees || empResp?.data || empResp || [];
          const matched = Array.isArray(list) ? list.find(e => e.tax_id === taxId) : null;
          employeeIdToSet = matched?.id;
        }
      } catch (e) {
        // silencioso
      }

      // Setear receptor oculto y empleado (si se encontró)
      form.setFieldsValue({
        receiver_id: invoice?.receiver_id || undefined,
        employee_id: employeeIdToSet || undefined,
      });

      // Precargar datos de nómina desde el JSON (soporta PascalCase y camelCase)
      const rawNomina = invoice?.nomina?.nomina_data ?? invoice?.nomina_data;
      const nomina = typeof rawNomina === 'string' ? JSON.parse(rawNomina || '{}') : (rawNomina || {});
      const emisor = nomina?.Emisor || {};
      const receptorN = nomina?.Receptor || {};

      const percepciones =
        Array.isArray(nomina?.Percepciones?.Percepcion)
          ? nomina.Percepciones.Percepcion.map(p => ({
              tipo: p.TipoPercepcion,
              clave: p.Clave,
              concepto: p.Concepto,
              gravado: Number(p.ImporteGravado ?? 0),
              exento: Number(p.ImporteExento ?? 0),
            }))
          : (Array.isArray(nomina?.percepciones) ? nomina.percepciones : []);

      const deducciones =
        Array.isArray(nomina?.Deducciones?.Deduccion)
          ? nomina.Deducciones.Deduccion.map(d => ({
              tipo: d.TipoDeduccion,
              clave: d.Clave,
              concepto: d.Concepto,
              importe: Number(d.Importe ?? 0),
            }))
          : (Array.isArray(nomina?.deducciones) ? nomina.deducciones : []);

      const otrosPagos =
        Array.isArray(nomina?.OtrosPagos?.OtroPago)
          ? nomina.OtrosPagos.OtroPago.map(o => ({
              tipo: o.TipoOtroPago,
              clave: o.Clave,
              concepto: o.Concepto,
              importe: Number(o.Importe ?? 0),
            }))
          : (Array.isArray(nomina?.otrosPagos) ? nomina.otrosPagos : []);

      const incapacidades =
        Array.isArray(nomina?.Incapacidades?.Incapacidad)
          ? nomina.Incapacidades.Incapacidad.map(i => ({
              tipo: i.TipoIncapacidad,
              dias: Number(i.DiasIncapacidad ?? 0),
              importe: Number(i.ImporteMonetario ?? 0),
            }))
          : (Array.isArray(nomina?.incapacidades) ? nomina.incapacidades : []);

      form.setFieldsValue({
        // Periodo y datos generales (lee PascalCase y camelCase)
        tipoNomina: nomina?.TipoNomina ?? nomina?.tipoNomina ?? 'O',
        fechaPago: nomina?.FechaPago ?? nomina?.fechaPago ?? undefined,
        numDiasPagados: nomina?.NumDiasPagados ?? nomina?.numDiasPagados ?? undefined,
        fechaInicialPago: nomina?.FechaInicialPago ?? nomina?.fechaInicialPago ?? undefined,
        fechaFinalPago: nomina?.FechaFinalPago ?? nomina?.fechaFinalPago ?? undefined,
        registroPatronal: emisor?.RegistroPatronal ?? nomina?.registroPatronal ?? undefined,

        // Datos del empleado
        curp: receptorN?.Curp ?? nomina?.curp ?? undefined,
        nss: receptorN?.NumSeguridadSocial ?? nomina?.nss ?? undefined,
        periodicidadPago: receptorN?.PeriodicidadPago ?? nomina?.periodicidadPago ?? undefined,
        fechaInicioRelLaboral: receptorN?.FechaInicioRelLaboral ?? nomina?.fechaInicioRelLaboral ?? undefined,
        antiguedad: receptorN?.Antigüedad ?? receptorN?.Antiguedad ?? nomina?.antiguedad ?? undefined,
        tipoContrato: receptorN?.TipoContrato ?? nomina?.tipoContrato ?? undefined,
        tipoRegimen: receptorN?.TipoRegimen ?? nomina?.tipoRegimen ?? undefined,
        numEmpleado: receptorN?.NumEmpleado ?? nomina?.numEmpleado ?? undefined,
        departamento: receptorN?.Departamento ?? nomina?.departamento ?? undefined,
        puesto: receptorN?.Puesto ?? nomina?.puesto ?? undefined,
        riesgoPuesto: receptorN?.RiesgoPuesto ?? nomina?.riesgoPuesto ?? undefined,
        salarioBaseCotApor: receptorN?.SalarioBaseCotApor ?? nomina?.salarioBaseCotApor ?? undefined,
        salarioDiarioIntegrado: receptorN?.SalarioDiarioIntegrado ?? nomina?.salarioDiarioIntegrado ?? undefined,
        claveEntFed: receptorN?.ClaveEntFed ?? nomina?.claveEntFed ?? undefined,

        // Listas
        percepciones,
        deducciones,
        otrosPagos,
        incapacidades,
      });

      // Recalcular totales con los valores precargados
      computeTotals(form.getFieldsValue());

      setLoadedInvoiceId(invoice?.id || invoiceId);
      setLoadedInvoiceData(invoice);
    } catch (error) {
      console.error('Error cargando borrador de nómina:', error);
      message.error('No se pudo cargar el borrador de nómina');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const resp = await companiesService.getCompanies();
      const list = resp?.data.companies || [];
      setCompanies(list);
    } catch (e) {
      console.error(e);
      setCompanies([]);
    }
  };

  const fetchReceivers = async (search = '', companyId = null) => {
    try {
      setLoadingReceivers(true);
      const resp = await employeesService.getEmployees(search, companyId);
      const list = resp?.employees || resp?.data || resp || [];
      setReceivers(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error al obtener empleados:', error);
      setReceivers([]);
    } finally {
      setLoadingReceivers(false);
    }
  };

  const fetchFolioSeries = async (companyId) => {
    if (!companyId) return;
    try {
      const resp = await folioSerieService.getFolioSeries(companyId, { enabled: true });
      setFolioSeries(resp?.data || []);
    } catch (e) {
      console.error(e);
      setFolioSeries([]);
    }
  };

  const handleCompanyChange = async (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (company?.tax_location) {
      form.setFieldsValue({ lugar_expedicion: company.tax_location });
    }
    await fetchFolioSeries(companyId);
    await fetchReceivers('', companyId);
  };

  // Asegurar que exista un receptor para el empleado (por RFC); si no existe, crear uno.
  const ensureReceiverForEmployee = async (emp) => {
    try {
      const listResp = await receiversService.getReceivers(emp.tax_id, { includeEmployees: true });
      const list = listResp?.data || listResp || [];
      const existing = Array.isArray(list) ? list.find(r => r.tax_id === emp.tax_id) : null;
      if (existing) return existing;

      const created = await receiversService.createReceiver({
        tax_id: emp.tax_id,
        tax_name: emp.tax_name,
        tax_location: emp.tax_location || '',
        use_tax: 'CN01',
        fiscal_regimen: emp.fiscal_regimen || '',
      });
      return created?.data || created;
    } catch (err) {
      console.error('Error asegurando receptor:', err);
      message.error('No se pudo vincular el receptor del empleado');
      return null;
    }
  };

  // Al seleccionar empleado, vincula/crea receptor y prellena campos de nómina.
  const handleReceiverSelect = async (employeeId) => {
    const emp = receivers.find(r => r.id === employeeId);
    if (!emp) return;

    const receiver = await ensureReceiverForEmployee(emp);
    const receiverId = receiver?.id;
    if (!receiverId) return;

    // Calcular antigüedad siempre en base a fecha de inicio y fecha final de pago (o hoy)
    const endDate = form.getFieldValue('fechaFinalPago') || dayjs().format('YYYY-MM-DD');
    const antiguedadVal = computeAntiguedadISO(emp.fecha_inicio_rel_laboral, endDate);

    form.setFieldsValue({
      receiver_id: receiverId,
      curp: emp.curp || '',
      nss: emp.nss || '',
      fechaInicioRelLaboral: emp.fecha_inicio_rel_laboral || '',
      antiguedad: antiguedadVal || '',
      tipoContrato: emp.tipo_contrato || '',
      tipoRegimen: emp.tipo_regimen || '',
      numEmpleado: emp.num_empleado || '',
      departamento: emp.departamento || '',
      puesto: emp.puesto || '',
      riesgoPuesto: emp.riesgo_puesto || '',
      claveEntFed: emp.clave_ent_fed || '',
      salarioBaseCotApor: emp.salario_base_cot_apor ?? undefined,
      salarioDiarioIntegrado: emp.salario_diario_integrado ?? undefined,
      periodicidadPago: emp.periodicidad_pago || undefined,
    });
  };

  const computeTotals = (values) => {
    const percepciones = values?.percepciones || [];
    const deducciones = values?.deducciones || [];
    const otrosPagos = values?.otrosPagos || [];

    const sumPercepciones = percepciones.reduce((acc, p) => acc + Number(p.gravado || 0) + Number(p.exento || 0), 0);
    const sumDeducciones = deducciones.reduce((acc, d) => acc + Number(d.importe || 0), 0);
    const sumOtrosPagos = otrosPagos.reduce((acc, o) => acc + Number(o.importe || 0), 0);

    // ISR suele tener TipoDeduccion '002'
    const totalISR = deducciones
      .filter(d => String(d.tipo || '').trim() === '002')
      .reduce((acc, d) => acc + Number(d.importe || 0), 0);

    const nominaTotal = sumPercepciones - sumDeducciones + sumOtrosPagos;

    setTotals({
      totalPercepciones: sumPercepciones,
      totalDeducciones: sumDeducciones,
      totalISR,
      totalOtrosPagos: sumOtrosPagos,
      nominaTotal,
    });
  };

  const onValuesChange = (changedValues, allValues) => {
    computeTotals(allValues);
    // Recalcular antigüedad si cambian fechas relevantes
    if (changedValues.fechaInicioRelLaboral || changedValues.fechaFinalPago) {
      const start = allValues.fechaInicioRelLaboral;
      const end = allValues.fechaFinalPago || dayjs().format('YYYY-MM-DD');
      if (start) {
        form.setFieldsValue({ antiguedad: computeAntiguedadISO(start, end) });
      }
    }
  };

  const buildNominaComplement = (values) => {
    const percepciones = values?.percepciones || [];
    const deducciones = values?.deducciones || [];
    const incapacidades = values?.incapacidades || [];
    let otrosPagos = values?.otrosPagos || [];

    // Validación NOM105: Si hay tipo "002" (Subsidio al empleo), no puede haber "007" o "008"
    const hasSubsidio = otrosPagos.some(o => String(o.tipo || '').trim() === '002');
    if (hasSubsidio) {
      const conflictTypes = otrosPagos.filter(o => ['007', '008'].includes(String(o.tipo || '').trim()));
      if (conflictTypes.length > 0) {
        message.warning('Se excluyeron otros pagos tipo 007/008 por incompatibilidad con subsidio al empleo (002)');
        otrosPagos = otrosPagos.filter(o => !['007', '008'].includes(String(o.tipo || '').trim()));
      }
    }

    const totalExento = percepciones.reduce((acc, p) => acc + Number(p.exento || 0), 0);
    const totalGravado = percepciones.reduce((acc, p) => acc + Number(p.gravado || 0), 0);

    const totalImpuestosRetenidos = deducciones
      .filter(d => String(d.tipo || '').trim() === '002')
      .reduce((acc, d) => acc + Number(d.importe || 0), 0);

    const totalOtrasDeducciones = deducciones
      .filter(d => String(d.tipo || '').trim() !== '002')
      .reduce((acc, d) => acc + Number(d.importe || 0), 0);

    const nomina = {
      TipoNomina: values.tipoNomina,
      FechaPago: values.fechaPago,
      FechaInicialPago: values.fechaInicialPago,
      FechaFinalPago: values.fechaFinalPago,
      NumDiasPagados: values.numDiasPagados,
      TotalPercepciones: Number(totals.totalPercepciones.toFixed(2)),
      TotalDeducciones: Number(totals.totalDeducciones.toFixed(2)),
      Emisor: values.registroPatronal ? { RegistroPatronal: values.registroPatronal } : undefined,
      Receptor: {
        Curp: values.curp,
        NumSeguridadSocial: values.nss,
        FechaInicioRelLaboral: values.fechaInicioRelLaboral,
        Antigüedad: computeAntiguedadISO(
          values.fechaInicioRelLaboral,
          values.fechaFinalPago || dayjs().format('YYYY-MM-DD')
        ),
        TipoContrato: values.tipoContrato,
        TipoRegimen: values.tipoRegimen,
        NumEmpleado: values.numEmpleado,
        Departamento: values.departamento,
        Puesto: values.puesto,
        RiesgoPuesto: values.riesgoPuesto,
        PeriodicidadPago: values.periodicidadPago,
        SalarioBaseCotApor: values.salarioBaseCotApor,
        SalarioDiarioIntegrado: values.salarioDiarioIntegrado,
        ClaveEntFed: values.claveEntFed,
      },
      Percepciones: percepciones.length > 0 ? {
        TotalSueldos: totals.totalPercepciones,
        TotalExento: totalExento,
        TotalGravado: totalGravado,
        Percepcion: percepciones.map(p => ({
          TipoPercepcion: p.tipo,
          Clave: p.clave,
          Concepto: p.concepto,
          ImporteGravado: Number(p.gravado || 0),
          ImporteExento: Number(p.exento || 0),
        })),
      } : undefined,
      Deducciones: deducciones.length > 0 ? {
        TotalImpuestosRetenidos: totalImpuestosRetenidos,
        TotalOtrasDeducciones: totalOtrasDeducciones,
        Deduccion: deducciones.map(d => ({
          TipoDeduccion: d.tipo,
          Clave: d.clave,
          Concepto: d.concepto,
          Importe: Number(d.importe || 0),
        })),
      } : undefined,
      Incapacidades: incapacidades.length > 0 ? {
        Incapacidad: incapacidades.map(i => ({
          TipoIncapacidad: i.tipo,
          DiasIncapacidad: Number(i.dias || 0),
          ImporteMonetario: Number(i.importe || 0),
        })),
      } : undefined,
      OtrosPagos: otrosPagos.length > 0 ? {
        OtroPago: otrosPagos.map(o => {
          const otroPago = {
            TipoOtroPago: o.tipo,
            Clave: o.clave,
            Concepto: o.concepto,
            Importe: Number(o.importe || 0),
          };
          
          // Si es tipo "002" (Subsidio al empleo), agregar nodo SubsidioAlEmpleo
          if (String(o.tipo || '').trim() === '002') {
            otroPago.SubsidioAlEmpleo = {
              SubsidioCausado: Number(o.importe || 0)
            };
          }
          
          return otroPago;
        }),
      } : undefined,
    };

    // Limpiar claves undefined
    Object.keys(nomina).forEach(k => {
      if (nomina[k] === undefined) delete nomina[k];
    });

    return nomina;
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const montoNomina = totals.nominaTotal;
      if (montoNomina <= 0) {
        message.warning('El total de nómina debe ser mayor a 0');
        setLoading(false);
        return;
      }

      // Construir complemento y payload SIEMPRE para poder comparar
      const nomina = buildNominaComplement(values);
      const selectedCompany = companies.find(c => c.id === values.company_id);
      const lugarExpedicion = selectedCompany?.tax_location;

      // Subtotal bruto = percepciones + otros pagos
      const subtotalBruto = totals.totalPercepciones + totals.totalOtrosPagos;
      // Descuento = total deducciones
      const descuentoTotal = totals.totalDeducciones;
      // Total = subtotal - descuento
      const totalCfdi = Number((subtotalBruto - descuentoTotal).toFixed(2));

      const payload = {
        company_id: values.company_id,
        receiver_id: values.receiver_id,
        folio_serie_id: values.folio_serie_id || undefined,
        fecha: values.fecha || new Date().toISOString().split('T')[0],
        metodo_pago: 'PUE',
        lugar_expedicion: lugarExpedicion,
        uso_cfdi: 'CN01',
        tipo_comprobante: 'N',
        moneda: values.moneda || 'MXN',
        condiciones_pago: values.condiciones_pago || undefined,
        subtotal: Number(subtotalBruto.toFixed(2)),
        descuento: Number(descuentoTotal.toFixed(2)),
        total: totalCfdi,
        conceptos: [
          {
            clave_prod_serv: '84111505',
            cantidad: 1,
            clave_unidad: 'ACT',
            descripcion: 'Pago de nómina',
            valor_unitario: Number(subtotalBruto.toFixed(2)),
            importe: Number(subtotalBruto.toFixed(2)),
            descuento: Number(descuentoTotal.toFixed(2)),
            objeto_imp: '01',
          }
        ],
        nomina,
      };

      if (loadedInvoiceId) {
        // Si hay borrador cargado, comparar contra DB y decidir
        const dbNomina = loadedInvoiceData?.nomina?.nomina_data;
        const nominaChanged = hasNominaChanges(dbNomina, nomina);

        const headerChanged =
          Number(loadedInvoiceData?.subtotal ?? 0).toFixed(2) !== Number(payload.subtotal).toFixed(2) ||
          Number(loadedInvoiceData?.descuento ?? 0).toFixed(2) !== Number(payload.descuento).toFixed(2) ||
          Number(loadedInvoiceData?.total ?? 0).toFixed(2) !== Number(payload.total).toFixed(2);

        if (nominaChanged || headerChanged) {
          // Crear nueva nómina con payload actualizado y timbrar
          const createResp = await userService.createInvoice(payload);
          const createdId = createResp?.id || createResp?.data?.id;
          if (!createdId) {
            message.error('No se pudo crear la nómina actualizada');
            return;
          }
          try {
            await userService.stampInvoice(createdId);
            message.success('Nómina actualizada y timbrada correctamente');
            navigate(`/invoices/${createdId}`);
          } catch (stampErr) {
            console.error(stampErr);
            const msg = stampErr.response?.data?.message || 'Nómina actualizada creada, pero no se pudo timbrar';
            message.warning(msg);
            navigate(`/invoices/${createdId}`);
          } finally {
            setLoading(false);
          }
          return;
        } else {
          // Sin cambios: timbrar la factura existente
          try {
            await userService.stampInvoice(loadedInvoiceId);
            message.success('Nómina timbrada correctamente');
            navigate(`/invoices/${loadedInvoiceId}`);
          } catch (stampErr) {
            console.error(stampErr);
            const msg = stampErr.response?.data?.message || 'No se pudo timbrar la nómina existente';
            message.error(msg);
          } finally {
            setLoading(false);
          }
          return;
        }
      }

      // Flujo normal: crear y timbrar
      const resp = await userService.createInvoice(payload);
      const createdId = resp?.id || resp?.data?.id;

      if (!createdId) {
        message.error('No se pudo crear la nómina');
        return;
      }

      try {
        await userService.stampInvoice(createdId);
        message.success('Nómina creada y timbrada correctamente');
      } catch (stampErr) {
        console.error(stampErr);
        const msg = stampErr.response?.data?.message || 'Nómina creada, pero no se pudo timbrar';
        message.warning(msg);
      }

      navigate(`/invoices/${createdId}`);
    } catch (error) {
      console.log('Error creating/stamping invoice:', error);
              let msg = error.response?.data?.error || 'Error al crear/timbrar la nomina';
              // Si el mensaje es del tipo "Error HTTP: {...} - {...}", extraemos el JSON y el mensaje interno
              if (typeof msg === 'string' && msg.startsWith('Error HTTP:')) {
                try {
                  // Extraer la parte del JSON antes del guión
                  const jsonPart = msg.substring('Error HTTP:'.length, msg.indexOf(' - {')).trim();
                  const errorObj = JSON.parse(jsonPart);
                  if (errorObj && errorObj.message) {
                    msg = errorObj.message;
                  }
                } catch (e) {
                  console.error('Error parsing error message:', e);
                }
              }
      
          message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/nominas')}>
          Regresar
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          Crear Nómina
        </Title>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={onValuesChange}
        initialValues={{
          uso_cfdi: 'CN01',
          tipoNomina: 'O',
          moneda: 'MXN',
          metodo_pago: 'PUE',
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8} lg={6}>
            <Card title="Datos del CFDI">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={24}>
                  <Form.Item name="company_id" label="Empresa" rules={[{ required: true }]}>
                    <Select placeholder="Selecciona empresa" onChange={handleCompanyChange}>
                      {companies.map(c => (
                        <Option key={c.id} value={c.id}>
                          {c.tax_name} ({c.tax_id})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={24}>
                  <Form.Item name="folio_serie_id" label="Serie/Folio">
                    <Select placeholder="Selecciona serie">
                      {folioSeries.map(f => (
                        <Option key={f.id} value={f.id}>
                        {f.folio} - {f.serie} 
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} md={16} lg={18}>
            <Card title="Periodo de Nómina">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="tipoNomina" label="Tipo de Nómina" rules={[{ required: true }]}>
                    <Select>
                      <Option value="O">O - Ordinaria</Option>
                      <Option value="E">E - Extraordinaria</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="fechaPago" label="Fecha de Pago" rules={[{ required: true }]}>
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="numDiasPagados" label="Días Pagados" rules={[{ required: true }]}>
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="fechaInicialPago" label="Fecha Inicial del Periodo" rules={[{ required: true }]}>
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="fechaFinalPago" label="Fecha Final del Periodo" rules={[{ required: true }]}>
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="registroPatronal" label="Registro Patronal">
                    <Input placeholder="Opcional" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Divider />

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={18}>
            <Card title="Datos del Empleado">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="employee_id" label="Empleado (Receptor)" rules={[{ required: true }]}>
                    <Select placeholder="Selecciona empleado" onChange={handleReceiverSelect}>
                      {receivers.map(r => (
                        <Option key={r.id} value={r.id}>
                          {r.tax_name} ({r.tax_id})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {/* Campo oculto para el id de receptor real que se usará en la creación de la factura */}
                  <Form.Item name="receiver_id" style={{ display: 'none' }}>
                    <Input type="hidden" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="curp" label="CURP" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}><Form.Item name="nss" label="NSS"><Input /></Form.Item></Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="periodicidadPago" label="Periodicidad de Pago" rules={[{ required: true }]}>
                    <Select placeholder="Selecciona periodicidad">
                      <Option value="02">02 - Semanal</Option>
                      <Option value="03">03 - Catorcenal</Option>
                      <Option value="04">04 - Quincenal</Option>
                      <Option value="05">05 - Mensual</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}><Form.Item name="fechaInicioRelLaboral" label="Fecha Inicio Rel. Laboral" rules={[{ required: true }]}><Input type="date" /></Form.Item></Col>
                <Col xs={24} sm={12} md={8}><Form.Item name="tipoContrato" label="Tipo Contrato" rules={[{ required: true }]}><Input placeholder="Ej: 01" /></Form.Item></Col>
                <Col xs={24} sm={12} md={8}><Form.Item name="tipoRegimen" label="Tipo Régimen" rules={[{ required: true }]}><Input placeholder="Ej: 03" /></Form.Item></Col>
                <Col xs={24} sm={12} md={8}><Form.Item name="numEmpleado" label="Num. Empleado" rules={[{ required: true }]}><Input /></Form.Item></Col>
                <Col xs={24} sm={12} md={8}><Form.Item name="departamento" label="Departamento" rules={[{ required: true }]}><Input /></Form.Item></Col>
                <Col xs={24} sm={12} md={8}><Form.Item name="puesto" label="Puesto" rules={[{ required: true }]}><Input /></Form.Item></Col>
                <Col xs={24} sm={12} md={8}><Form.Item name="riesgoPuesto" label="Riesgo Puesto"><Input placeholder="Ej: 1" /></Form.Item></Col>
                <Col xs={24} sm={12} md={8}><Form.Item name="claveEntFed" label="Clave Entidad Fed." rules={[{ required: true }]}><Input placeholder="Ej: CM" /></Form.Item></Col>
                <Col xs={24} sm={12} md={8}><Form.Item name="salarioBaseCotApor" label="Salario Base Cot. Apor." rules={[{ required: true }]}><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item></Col>
                <Col xs={24} sm={12} md={8}><Form.Item name="salarioDiarioIntegrado" label="Salario Diario Integrado" rules={[{ required: true }]}><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item></Col>
              </Row>
            </Card>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card title="Percepciones">
                  <Form.List name="percepciones">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...rest }) => (
                          <Row key={key} gutter={[8, 8]} align="middle" style={{ marginBottom: 16, borderBottom: `1px solid ${token.colorSplit}`, paddingBottom: 16 }}>
                            <Col xs={24} sm={12} md={4}>
                              <Form.Item {...rest} name={[name, 'tipo']} label="Tipo" rules={[{ required: true }]}>
                                <Input placeholder="Ej: 001" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={4}>
                              <Form.Item {...rest} name={[name, 'clave']} label="Clave" rules={[{ required: true }]}>
                                <Input />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={8}>
                              <Form.Item {...rest} name={[name, 'concepto']} label="Concepto" rules={[{ required: true }]}>
                                <Input />
                              </Form.Item>
                            </Col>
                            <Col xs={12} sm={8} md={3}>
                              <Form.Item {...rest} name={[name, 'gravado']} label="Gravado" rules={[{ required: true }]}>
                                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                            <Col xs={12} sm={8} md={3}>
                              <Form.Item {...rest} name={[name, 'exento']} label="Exento" rules={[{ required: true }]}>
                                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={8} md={2} style={{ textAlign: 'right' }}>
                              <Button icon={<DeleteOutlined />} danger onClick={() => remove(name)} />
                            </Col>
                          </Row>
                        ))}
                        <Button
                          icon={<PlusOutlined />}
                          onClick={() => add({ tipo: '', clave: '', concepto: '', gravado: 0, exento: 0 })}
                          block
                        >
                          Agregar Percepción
                        </Button>
                      </>
                    )}
                  </Form.List>
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card title="Deducciones">
                  <Form.List name="deducciones">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...rest }) => (
                          <Row key={key} gutter={[8, 8]} align="middle" style={{ marginBottom: 16, borderBottom: `1px solid ${token.colorSplit}`, paddingBottom: 16 }}>
                            <Col xs={24} sm={12} md={4}>
                              <Form.Item {...rest} name={[name, 'tipo']} label="Tipo" rules={[{ required: true }]}>
                                <Input placeholder="Ej: 002 (ISR)" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={4}>
                              <Form.Item {...rest} name={[name, 'clave']} label="Clave" rules={[{ required: true }]}>
                                <Input />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={8}>
                              <Form.Item {...rest} name={[name, 'concepto']} label="Concepto" rules={[{ required: true }]}>
                                <Input />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Form.Item {...rest} name={[name, 'importe']} label="Importe" rules={[{ required: true }]}>
                                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={2} style={{ textAlign: 'right' }}>
                              <Button icon={<DeleteOutlined />} danger onClick={() => remove(name)} />
                            </Col>
                          </Row>
                        ))}
                        <Button
                          icon={<PlusOutlined />}
                          onClick={() => add({ tipo: '', clave: '', concepto: '', importe: 0 })}
                          block
                        >
                          Agregar Deducción
                        </Button>
                      </>
                    )}
                  </Form.List>
                </Card>
              </Col>
            </Row>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card title="Otros Pagos">
                  <Form.List name="otrosPagos">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...rest }) => (
                          <Row key={key} gutter={[8, 8]} align="middle" style={{ marginBottom: 16, borderBottom: `1px solid ${token.colorSplit}`, paddingBottom: 16 }}>
                            <Col xs={24} sm={12} md={4}>
                              <Form.Item {...rest} name={[name, 'tipo']} label="Tipo">
                                <Input placeholder="Ej: 002" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={4}>
                              <Form.Item {...rest} name={[name, 'clave']} label="Clave">
                                <Input />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={8}>
                              <Form.Item {...rest} name={[name, 'concepto']} label="Concepto">
                                <Input />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Form.Item {...rest} name={[name, 'importe']} label="Importe">
                                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={2} style={{ textAlign: 'right' }}>
                              <Button icon={<DeleteOutlined />} danger onClick={() => remove(name)} />
                            </Col>
                          </Row>
                        ))}
                        <Button
                          icon={<PlusOutlined />}
                          onClick={() => add({ tipo: '', clave: '', concepto: '', importe: 0 })}
                          block
                        >
                          Agregar Otro Pago
                        </Button>
                      </>
                    )}
                  </Form.List>
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card title="Incapacidades">
                  <Form.List name="incapacidades">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...rest }) => (
                          <Row key={key} gutter={[8, 8]} align="middle" style={{ marginBottom: 16, borderBottom: `1px solid ${token.colorSplit}`, paddingBottom: 16 }}>
                            <Col xs={24} sm={12} md={6}>
                              <Form.Item {...rest} name={[name, 'tipo']} label="Tipo">
                                <Input placeholder="Ej: 01" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Form.Item {...rest} name={[name, 'dias']} label="Días">
                                <InputNumber min={0} style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={10}>
                              <Form.Item {...rest} name={[name, 'importe']} label="Importe">
                                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={2} style={{ textAlign: 'right' }}>
                              <Button icon={<DeleteOutlined />} danger onClick={() => remove(name)} />
                            </Col>
                          </Row>
                        ))}
                        <Button
                          icon={<PlusOutlined />}
                          onClick={() => add({ tipo: '', dias: 0, importe: 0 })}
                          block
                        >
                          Agregar Incapacidad
                        </Button>
                      </>
                    )}
                  </Form.List>
                </Card>
              </Col>
            </Row>
          </Col>

          <Col xs={24} lg={6}>
            <Card>
              <Row gutter={[16, 16]}>
                <Col xs={12} lg={24}><Text>Total Percepciones: </Text><Text strong>${totals.totalPercepciones.toFixed(2)}</Text></Col>
                <Col xs={12} lg={24}><Text>Total Deducciones: </Text><Text strong>${totals.totalDeducciones.toFixed(2)}</Text></Col>
                <Col xs={12} lg={24}><Text>Total ISR: </Text><Text strong>${totals.totalISR.toFixed(2)}</Text></Col>
                <Col xs={12} lg={24}><Text>Total Otros Pagos: </Text><Text strong>${totals.totalOtrosPagos.toFixed(2)}</Text></Col>
              </Row>
              <Divider />
              <Row>
                <Col span={24}><Title level={4} style={{ fontSize: '1.2rem' }}>Total Nómina: ${totals.nominaTotal.toFixed(2)}</Title></Col>
              </Row>

              <Space style={{ marginTop: 16, width: '100%' }} direction="vertical">
                <Button type="primary" htmlType="submit" loading={loading} block>
                  Timbrar Nómina
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default CreateNomina;
